import React, { useContext, useEffect } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';

import { buildInternalProductNameString } from '../../utils/optionNameUtils';
import { getCoverPricingData } from '../../utils/coverDetailsUtils';

import PageTemplate from '../../templates/PageTemplate';
import { DocumentTitle } from '../../components/atoms';
import { StepContext, steps, Step } from '../../contexts/StepContext';
import { useStyles } from './ProcessingStyles';

export const Processing: React.FC = () => {
  const { updateShowStepper, updateData, updateActiveStep } = useContext(StepContext);
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      history.replace('/');
      return;
    }

    updateActiveStep(3);
    window.scrollTo(0, 0);
    updateShowStepper(false);

    if (location.pathname === '/cancel/') {
      expire();
    } else {
      ingest();
    }
  }, []);

  const retrieveQuoteData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_RETRIEVE_QUOTE_ENDPOINT}/${sessionId}/${Cookies.get('GFSessionControl')}`,
      );


      if (response.data.err) throw new Error(response.data.err);

      const retrievedData = response.data.data;

      const currentEpochTime = Math.floor(Date.now() / 1000);
      if (retrievedData.ttlTimeout && retrievedData.ttlTimeout < currentEpochTime) {
        history.replace('/timeout');
        return null;
      }

      const quoteDataReactFormat = {
        source: retrievedData.origin_site,
        title: retrievedData.customer.title,
        firstName: retrievedData.customer.first_name,
        lastName: retrievedData.customer.last_name,
        dateOfBirth: retrievedData.customer.date_of_birth,
        emailAddress: retrievedData.customer.email,
        phoneNumber: retrievedData.customer.telephone,
        postcode: retrievedData.customer.address.postcode,
        addressLine1: retrievedData.customer.address.house,
        addressLine2: retrievedData.customer.address.street,
        addressLine3: retrievedData.customer.address.town,
        registrationNumber: retrievedData.vehicle.registration,
        vehicleMake: retrievedData.vehicle.make,
        vehicleModel: retrievedData.vehicle.model,
        vehicleAge: retrievedData.vehicle_age_yrs,
        productName: retrievedData.product_name,
        personalCover: retrievedData.product_name.includes('Personal') ? 'True' : 'False',
        coverPrice: retrievedData.Premium,
        policyStartDate: retrievedData.start_date,
        partnerTitle: retrievedData.partner.title,
        partnerInitial: retrievedData.partner.initial,
        partnerSurname: retrievedData.partner.surname,
        quote: retrievedData.quote_id,
        quoteId: retrievedData.quote_id,
        checkoutId: sessionId,
        hash: Cookies.get('GFSessionControl'),
      };

      const stateDataObject = await getPricingData(quoteDataReactFormat);
      updateData(stateDataObject);

      return quoteDataReactFormat;
    } catch (error: any) {
      window.location.replace('/timeout');
      return null;

    }
  };

  const getPricingData = async (quoteData) => {
    try {
      const { data: options } = await axios.get(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_OPTIONS_ENDPOINT}`,
      );

      const productName = buildInternalProductNameString(quoteData.productName, quoteData.personalCover);
      const { coverPrice, baseOption } = getCoverPricingData(options.Items, productName, +quoteData.vehicleAge);

      const stateDataObjectInternal = {
        ...quoteData,
        productName,
        coverPrice,
        baseOption,
        hasVoluntaryExcess: productName.toLowerCase().split(' + ').includes('excess') ? 'Yes' : 'No',
        options: options.Items,
      };
      return stateDataObjectInternal;
    } catch (err) {
      history.replace("https://www.greenflag.com");
    }
  };

  const ingest = async () => {
    const quote = await retrieveQuoteData();
    if (!quote) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_INGEST_ENDPOINT}`,
        { checkoutId: quote?.checkoutId || sessionId },
      );

      if (response.status === 200) {
        history.push('/all-sorted');
      } else {
        history.push('/failedpayment');
      }
    } catch {
      history.push('/failedpayment');
    }

  };

  const expire = async () => {
    const quote = await retrieveQuoteData();
    if (!quote) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_EXPIRE_CHECKOUT_ENDPOINT}`,
        { checkoutId: quote?.checkoutId || sessionId },
      );


    } catch (err) {
      // We dont care if this succeeds or fails. 
    }

    updateActiveStep(2);
    history.push(steps[Step.REVIEW].url);
  };

  return (
    <PageTemplate>
      <DocumentTitle title={`DLG ${process.env.REACT_APP_SITE_ID?.toUpperCase()} - Processing`} />
      <Grid container className={classes.gridMainContainer}>
        <Grid item xs={12} sm={5} lg={6} className={classes.gridMain}>
          <Typography className={classes.heading} variant="h1">
            ONE SECOND...
          </Typography>
          <Typography className="pt1">Please don&apos;t close this window or click back in the browser.</Typography>
        </Grid>
      </Grid>
    </PageTemplate>
  );
};

export default Processing;
